import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApprovalCancelled, useApproval } from "@/app/approval";
import { usePos } from "@/app/context";
import { Modal } from "@/components/Modal";
import { MoneyText } from "@/components/MoneyText";
import { NumPad } from "@/components/NumPad";
import { call } from "@/lib/api";
import { errorText } from "@/lib/errors";
import type { CashMovement } from "@/lib/types";

export type MovementType = "pay_in" | "pay_out";

const REASONS: Record<MovementType, readonly string[]> = {
  pay_in: ["change_refill", "other"],
  pay_out: ["bank_deposit", "expense", "change_refill", "other"],
};

/**
 * 入金 / 出金 (design/05 §3.7): the amount on the big pad, a reason from the list (+ a note).
 * 出金 needs the manager — the server answers `manager_approval_required` and the approval
 * dialog asks for the manager's PIN (T07-18). The reason is kept in Japanese (the 日計表 is read
 * by the 税理士).
 */
export function CashMovementModal({
  type,
  shiftId,
  onClose,
  onDone,
}: {
  type: MovementType;
  shiftId: string;
  onClose: () => void;
  onDone: (movement: CashMovement) => void;
}) {
  const { t, i18n } = useTranslation();
  const { api } = usePos();
  const { withApproval } = useApproval();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState<string>(REASONS[type][0] ?? "other");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const value = Number.parseInt(amount || "0", 10);
  const needsNote = reason === "other";
  const ready = value > 0 && (!needsNote || note.trim().length > 0) && !busy;

  const submit = async () => {
    if (!ready) {
      return;
    }
    setBusy(true);
    setError(null);
    const ja = i18n.getFixedT("ja");
    const label = ja(`shift.reason_${reason}`);
    const text = note.trim() ? `${label} — ${note.trim()}` : label;
    try {
      const movement = await withApproval(
        (token) =>
          call(() =>
            api.POST("/v1/pos/shifts/{shift_id}/cash-movements", {
              params: { path: { shift_id: shiftId } },
              body: { type, amount: value, reason: text.slice(0, 200) },
              headers: token ? { "X-Approval-Token": token } : {},
            }),
          ),
        { subject: shiftId, amount: value },
      );
      onDone(movement);
    } catch (err) {
      setError(err instanceof ApprovalCancelled ? t("approval.cancelled") : errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      size="lg"
      title={type === "pay_in" ? t("shift.pay_in_title") : t("shift.pay_out_title")}
      description={type === "pay_in" ? t("shift.pay_in_help") : t("shift.pay_out_help")}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="min-h-pos-touch rounded-md border px-6 text-lg"
          >
            {t("app.cancel")}
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!ready}
            className="min-h-pos-touch rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground disabled:opacity-50"
          >
            {busy
              ? t("app.saving")
              : type === "pay_in"
                ? t("shift.pay_in_submit")
                : t("shift.pay_out_submit")}
          </button>
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <p className="text-sm text-muted-foreground">{t("shift.movement_amount")}</p>
          <MoneyText amount={value} size="xl" data-testid="movement-amount" />
          <NumPad
            label={t("shift.movement_amount")}
            value={amount}
            onChange={setAmount}
            money
            maxLength={7}
            disabled={busy}
          />
        </div>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 font-medium">{t("shift.movement_reason")}</legend>
          {REASONS[type].map((code) => (
            <button
              key={code}
              type="button"
              aria-pressed={reason === code}
              onClick={() => setReason(code)}
              className="min-h-pos-touch rounded-md border px-4 text-left text-lg aria-pressed:border-primary aria-pressed:bg-accent"
            >
              {t(`shift.reason_${code}`)}
            </button>
          ))}
          <label className="mt-2 flex flex-col gap-1">
            <span className="text-sm">
              {needsNote ? t("shift.movement_note_required") : t("shift.movement_note")}
            </span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={120}
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
          {error ? (
            <p role="alert" className="text-pos-problem">
              {error}
            </p>
          ) : null}
        </fieldset>
      </div>
    </Modal>
  );
}
